import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #contentObserver = new MutationObserver(() => this.#activateContent());
  #refreshEpoch = 0;
  #hasBeenMobile = false;

  #syncTrigger() {
    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    if (!trigger) return;
    if (isMobileBreakpoint()) trigger.removeAttribute('aria-haspopup');
    else trigger.setAttribute('aria-haspopup', 'dialog');
  }

  #activateContent() {
    if (isMobileBreakpoint() || this.#hasBeenMobile) return;
    const template = this.querySelector('.cart-drawer__inner > template[data-sc-desktop-cart-content]');
    if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
    const dialog = this.refs.dialog;
    const emptyHeading = dialog?.querySelector('.cart-drawer__inner #cart-drawer-heading-empty');
    const fullHeading = dialog?.querySelector('.cart-drawer__inner #cart-drawer-heading');
    const heading = emptyHeading || fullHeading;
    if (heading) {
      dialog.classList.toggle('cart-drawer--empty', Boolean(emptyHeading));
      dialog.setAttribute('aria-labelledby', heading.id);
    }
    if (dialog?.open) this.#updateStickyState();
  }

  async #refreshContent() {
    const epoch = ++this.#refreshEpoch;
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(8000) });
      if (!response.ok) return false;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('cart-drawer-component .cart-drawer__inner');
      const current = this.querySelector('.cart-drawer__inner');
      if (!fresh || !current || !this.isConnected || epoch !== this.#refreshEpoch || isMobileBreakpoint()) return false;
      const freshDialog = html.querySelector('cart-drawer-component dialog');
      if (freshDialog) {
        this.refs.dialog.classList.toggle('cart-drawer--empty', freshDialog.classList.contains('cart-drawer--empty'));
        const label = freshDialog.getAttribute('aria-labelledby');
        if (label) this.refs.dialog.setAttribute('aria-labelledby', label);
      }
      current.replaceChildren(...fresh.childNodes);
      this.#hasBeenMobile = false;
      this.#activateContent();
      return true;
    } catch (_) { return false; }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#hasBeenMobile = isMobileBreakpoint();
    this.#syncTrigger();
    this.#contentObserver.observe(this, { childList: true, subtree: true });
    this.#activateContent();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpoint);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#guardMobileOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#contentObserver.disconnect();
    ++this.#refreshEpoch;
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpoint);
    this.removeEventListener(DialogOpenEvent.eventName, this.#guardMobileOpen);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#historyAbortController?.abort();
  }

  #guardMobileOpen = () => {
    if (isMobileBreakpoint()) this.#handleBreakpoint();
  };

  #handleBreakpoint = async () => {
    this.#syncTrigger();
    ++this.#refreshEpoch;
    if (!isMobileBreakpoint()) {
      if (this.#hasBeenMobile) await this.#refreshContent();
      else this.#activateContent();
      return;
    }
    this.#hasBeenMobile = true;
    if (this.refs.dialog?.open) await this.closeDialog();
    if (!isMobileBreakpoint()) return;
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner || inner.querySelector(':scope > template[data-sc-desktop-cart-content]')) return;
    const template = document.createElement('template');
    template.setAttribute('data-sc-desktop-cart-content', '');
    template.content.append(...inner.childNodes);
    inner.append(template);
  };

  showDialog() {
    if (isMobileBreakpoint()) return;
    super.showDialog();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = async (event) => {
    if (event instanceof CartAddEvent && !event.detail.data?.didError && this.hasAttribute('auto-open')) {
      if (this.#hasBeenMobile && !isMobileBreakpoint()) await this.#refreshContent();
      if (!this.#hasBeenMobile) this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  async open(event) {
    if (isMobileBreakpoint() || event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey) return;
    event?.preventDefault();
    if (this.#hasBeenMobile && !(await this.#refreshContent())) {
      window.location.assign(Theme.routes.cart_url);
      return;
    }
    this.#activateContent();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

